import { Component, type ErrorInfo, type ReactNode } from "react"
import { useLocation } from "react-router-dom"
import * as Sentry from "@sentry/react"
import { RefreshCw } from "lucide-react"
import { Button } from "@/components/ui/button"
import { capturePostHogException } from "@/lib/posthog-client"

// A route chunk from a previous deploy that no longer exists on the server.
// Not an application bug: the recovery is fetching the current index.html.
const STALE_CHUNK_RE =
  /Failed to fetch dynamically imported module|Unable to preload CSS|Importing a module script failed/i

interface BoundaryProps {
  children: ReactNode
  /** Current pathname; an in-error boundary resets when it changes, so the
      sidebar (which stays mounted outside the inner boundary) keeps working. */
  locationKey: string
}

interface BoundaryState {
  error: Error | null
}

class Boundary extends Component<BoundaryProps, BoundaryState> {
  state: BoundaryState = { error: null }

  static getDerivedStateFromError(error: Error): BoundaryState {
    return { error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Stale chunks are expected noise in the minutes after every deploy;
    // reporting them would bury real errors in Sentry.
    if (!STALE_CHUNK_RE.test(error.message)) {
      Sentry.captureException(error, { extra: { componentStack: errorInfo.componentStack } })
      capturePostHogException(error)
    }
  }

  componentDidUpdate(prevProps: BoundaryProps) {
    if (this.state.error && prevProps.locationKey !== this.props.locationKey) {
      // React.lazy caches a rejected import permanently, so re-rendering the
      // failed route cannot succeed — but any OTHER route's chunk can, and
      // the failed one recovers on the next full page load.
      this.setState({ error: null })
    }
  }

  render() {
    const { error } = this.state
    if (!error) {
      return this.props.children
    }

    const staleChunk = STALE_CHUNK_RE.test(error.message)
    return (
      <div className="flex min-h-[50vh] flex-col items-center justify-center gap-4 p-6 text-center">
        <p className="text-sm text-muted-foreground">
          {staleChunk
            ? "A new version of Forge was released — reload to pick it up."
            : "Something went wrong loading this page."}
        </p>
        <Button variant="outline" size="sm" onClick={() => window.location.reload()}>
          <RefreshCw className="mr-2 h-4 w-4" />
          Reload
        </Button>
      </div>
    )
  }
}

/**
 * Catches render-time errors from lazy routes so a failed chunk load degrades
 * to a message instead of React unmounting the whole root (the white screen).
 *
 * There is no in-place retry to offer: recovery from a stale chunk is always
 * a page load, either the automatic one from the vite:preloadError handler in
 * main.tsx or the button here when that reload already spent its window.
 */
export function RouteErrorBoundary({ children }: { children: ReactNode }) {
  const location = useLocation()
  return <Boundary locationKey={location.pathname}>{children}</Boundary>
}
